// ============================================
// DispatchDeck — PO field helpers (TASK-4828)
// ============================================
// A PO number is how a customer's AP department finds the invoice. A wrong one
// is an invoice that sits unpaid while everybody assumes it arrived. That makes
// this a MONEY FIELD, and the two rules TASK-4782 established for it live here
// so there is exactly ONE implementation instead of one per view:
//
//   1. SUGGEST, NEVER REWRITE — the only transform applied to a stored value is
//      trim. Stripping a "PO #" prefix (221 live documents carry it on purpose),
//      changing case, splitting two POs out of one field, blanking "UNKNOWN" —
//      every one of those is a judgement that changes what the customer's AP
//      department is being asked to match, and it belongs to a human.
//   2. WARN, NEVER BLOCK at the point of entry. Missing or awkward paperwork
//      never stops the truck. (The QuickBooks PUSH does refuse over the cap —
//      see functions/src/qbo.js — because a wrong PO on a real invoice is worse
//      than a refusal the biller can see. That is a different moment.)
//
// TASK-4782 shipped these as module-local functions in `views/dispatch.js` for
// the job form. TASK-4828 needs the identical behaviour on the ticket review
// modal (billing.js) and on the project standing PO (projects.js), and three
// copies of a money-field normalizer is how a money field rots. This module is
// the canonical home; `dispatch.js` still holds its own copy and
// `tests/qbo-po-number.unit.cjs` FAILS if the two ever diverge in BEHAVIOUR.
// dispatch.js copy in favour of this import is a one-line follow-up that lane
// could not make because dispatch.js was held by another lane.

// QuickBooks Online caps Invoice.PONumber at 15 characters.
//
// 🔴 HONESTY LABEL, and it is deliberate that it sits in the source rather than
// only in a task row: this number comes from Intuit's published Invoice entity
// reference. It has NOT been confirmed against a live rejection from the
// QuickBooks API — DispatchDeck has never pushed a single invoice to QBO (0 of
// 1,059 live tickets carry any `qbo*` field, measured 2026-09-09), and no
// reachable sandbox connection exists to test one. Treat it as documented, not
// proven. If a real push ever comes back with error 6000 naming a different
// length, THIS constant is the thing to change and the tests below will point
// at every place that reads it.
export const QBO_PO_MAX_CHARS = 15;

// Unicode whitespace class used for trimming. Plain `.trim()` does NOT strip
// NBSP or the zero-width characters, and a PO that differs from another only by
// a character nobody can see is the exact failure this is here to stop.
const _PO_EDGE_WS = /^[\s\u00A0\u2000-\u200B\uFEFF]+|[\s\u00A0\u2000-\u200B\uFEFF]+$/g;

/**
 * The ONLY transform applied to a PO on its way into storage: trim, and stop.
 *
 * INTERNAL whitespace is left alone — '26-134  -- NOT 26-99' keeps its double
 * space, because collapsing it is editing somebody's note, not tidying a field.
 * NEVER truncates: an over-length PO is stored exactly as typed and warned
 * about, because silently cutting it produces a number that matches nothing.
 */
export function normalizePoForSave(raw) {
  return String(raw == null ? '' : raw).replace(_PO_EDGE_WS, '');
}

/**
 * The COMPARISON key. Answers one question: "is what you just typed another
 * spelling of a PO this customer already uses?" Never stored, never suggested,
 * never written anywhere.
 *
 * FOLDS CASE AND WHITESPACE ONLY. PUNCTUATION STAYS SIGNIFICANT — smartAssistant.js
 * already recorded that PO-12345 and PO12345 are genuinely different POs, and a
 * key that stripped punctuation would offer to swap one for the other on a money
 * field. Measured cost of that choice (all 88 tenants, 2026-09-08): folding
 * punctuation too would group 15 live spelling-collision groups; folding only
 * case + whitespace groups 13. A missed suggestion costs one keystroke; a wrong
 * one costs an invoice.
 */
export function poGroupKey(v) {
  return String(v == null ? '' : v)
    .normalize('NFKC')
    .toLowerCase()
    .replace(/[\s\u00A0\u2000-\u200B\uFEFF]+/g, '');
}

/**
 * What, if anything, to tell the user about the PO they just typed. Returns
 * findings only — never mutates the value, never sets a blocking flag.
 * `existingPos` is the PO vocabulary already in use for THIS customer.
 */
export function poEntryIssues(value, existingPos) {
  const trimmed = normalizePoForSave(value);
  const out = { value: trimmed, len: trimmed.length, tooLong: false, existingSpelling: null };
  if (!trimmed) return out;

  out.tooLong = trimmed.length > QBO_PO_MAX_CHARS;

  const key = poGroupKey(trimmed);
  if (key) {
    for (const candidate of (existingPos || [])) {
      const c = normalizePoForSave(candidate);
      if (!c || c === trimmed) continue;
      if (poGroupKey(c) === key) { out.existingSpelling = c; break; }
    }
  }
  return out;
}

/**
 * The hint markup for a PO input, rendered into a host element the caller owns.
 * Reuses the existing `.smart-warning` styles so it sits in the same visual
 * family as the rest of the form guidance — nothing new in styles.css.
 *
 * `onUseExisting` is a JS expression string invoked with the chosen spelling in
 * `this.dataset.po`; pass null to render the over-length warning only.
 */
export function renderPoHintHTML(issues, opts) {
  const o = opts || {};
  const escHtml = o.escapeHtml || ((s) => String(s == null ? '' : s).replace(/[&<>"']/g, (m) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[m])));
  // A PO lands inside value="" / data-po="" — escapeHtml does NOT escape quotes,
  // so the attribute path must go through escAttr.
  // [[lesson_escapehtml_is_not_attribute_safe]]
  const escA = o.escAttr || (typeof window !== 'undefined' && window.escAttr) || escHtml;
  const rows = [];

  if (issues.existingSpelling && o.onUseExisting) {
    rows.push(
      '<div class="smart-warning smart-warning--similar">' +
      '<span class="material-icons smart-warning-icon">merge_type</span>' +
      '<span class="smart-warning-text">This customer already uses <strong>' + escHtml(issues.existingSpelling) + '</strong>. ' +
      'Same PO, different spelling — matching it keeps the billing together.</span>' +
      '<button type="button" class="smart-warning-action" data-po="' + escA(issues.existingSpelling) + '" ' +
      'onclick="' + o.onUseExisting + '">Use it</button>' +
      '</div>'
    );
  }

  if (issues.tooLong) {
    // WARN, NOT BLOCK, and say what actually happens. The user can save this
    // exactly as typed; what they cannot do is get it into QuickBooks.
    rows.push(
      '<div class="smart-warning smart-warning--duplicate">' +
      '<span class="material-icons smart-warning-icon">warning</span>' +
      // COD round 2, Code Reviewer — this used to say "until it is shortened",
      // which contradicted the corrected server message and, worse, invited the
      // one edit nobody should make on a money field: trimming a REAL PO to fit
      // invents a number the customer's AP department has never seen.
      '<span class="smart-warning-text"><strong>' + issues.len + ' characters — QuickBooks accepts ' + QBO_PO_MAX_CHARS + '.</strong> ' +
      'This saves fine, but QuickBooks will refuse the invoice while it is this long. ' +
      'If part of this is a note or a second PO, move that out and leave just the PO here. ' +
      'Do not shorten a real PO number to make it fit.</span>' +
      '</div>'
    );
  }

  return rows.join('');
}

// Also hung on window so a non-module caller (and any future view) can reach the
// same single implementation without a second `?v=` import instance.
// Pitfall #1: two `?v=` spellings of one module are two INSTANCES. These are
// pure functions with zero module state, so a duplicate instance is harmless —
// but the window handle removes the temptation to copy the code instead.
if (typeof window !== 'undefined') {
  window.PoField = { QBO_PO_MAX_CHARS, normalizePoForSave, poGroupKey, poEntryIssues, renderPoHintHTML };
}
